import React, { useState, useMemo, useCallback } from 'react';
import {
  pastorTenures as tenuresApi,
  pastorPostings as postingsApi,
  pastorHandovers as handoversApi,
  batch
} from '../../api/endpoints';
import type { BatchOperation } from '../../api/endpoints';
import {
  useMultiLedger, useSearch, usePagination, useNotification, useOptionList, useDateRangeFilter,
  useOpenRecordFromLink, findLedgerRow, RECORD_GONE
} from '../../hooks';
import { formatDate } from '../../utils/format';
import { printableRows, printReference } from '../../utils/printRows';
import useStyles from '../../theme/useStyles';
import { HANDOVER_SECTIONS, emptyChecklist } from './handoverChecklist';
import {
  PageHeader, SubTabs, SearchBar, DateRangeFilter, Pagination, Notification, PromptDialog, FormLayout, Input, Select, SearchableSelect, Badge, Button, TablePanel, Drawer
} from '../../components/ui';
import PrintButton from '../../components/ui/PrintButton';
import PastorSummary from './PastorSummary';

const TENURE_STATUSES = [
  { value: 'Current', label: 'Current (Active)' },
  { value: 'Transferred', label: 'Transferred' },
  { value: 'Retired', label: 'Retired' },
  { value: 'Deceased', label: 'Deceased' }
];
const HANDOVER_STATUSES = [
  { value: 'Pending', label: 'Pending Validation' },
  { value: 'Completed', label: 'Completed & Signed' }
];

const EMPTY_TENURE = {
  name: '', designation: '', apptOrder: '', startDate: '', endDate: '',
  prevChurch: '', nextChurch: '', status: 'Transferred', remarks: ''
};
const EMPTY_POSTING = { name: '', designation: '', diocese: '', orderDate: '', effectiveDate: '', reason: '' };
const EMPTY_HANDOVER = {
  date: '', outgoing: '', incoming: '', witness: '', approvedBy: '', status: 'Pending',
  ...emptyChecklist()
};

// "3 Yrs 4 Mos" between two dates; open-ended tenures run to today.
const durationBetween = (start, end) => {
  if (!start) return '-';
  const from = new Date(start);
  const to = end ? new Date(end) : new Date();
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return '-';

  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  if (months < 0) { years -= 1; months += 12; }
  if (years <= 0 && months <= 0) return 'Less than a month';

  const parts = [];
  if (years > 0) parts.push(`${years} Yrs`);
  if (months > 0) parts.push(`${months} Mos`);
  return parts.join(' ');
};

/** The three sibling resources behind this screen. */
type PastorTab = 'tenure' | 'posting' | 'handover';
/** The Summary tab reads all three; the rest of the screen works on one. */
type PastorView = PastorTab | 'summary';

const PastorHistory = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeView, setActiveView] = useState<PastorView>('summary');
  const isSummary = activeView === 'summary';
  const activeTab: PastorTab = isSummary ? 'tenure' : activeView;
  const [viewMode, setViewMode] = useState('history');
  const [tenureForm, setTenureForm] = useState(EMPTY_TENURE);
  const [postForm, setPostForm] = useState(EMPTY_POSTING);
  const [handoverForm, setHandoverForm] = useState(EMPTY_HANDOVER);
  const [editingId, setEditingId] = useState(null);
  // The record open in the View drawer.
  const [viewing, setViewing] = useState<Record<string, any> | null>(null);

  const isEditing = editingId !== null;
  const isDeletedView = viewMode === 'deleted';
  const { notification, notify, dismiss } = useNotification();

  const ledger = useMultiLedger({
    tenure: { resource: tenuresApi, label: 'Tenure', prefix: 'TNR', statusField: 'tenureStatus' },
    posting: { resource: postingsApi, label: 'Posting', prefix: 'PST', statusField: 'postingStatus' },
    handover: { resource: handoversApi, label: 'Handover', prefix: 'PHV', statusField: 'handoverStatus' }
  }, { notify });

  const designations = useOptionList('church_ph_desig');

  const dataset = isDeletedView ? ledger.voided(activeTab) : ledger.active(activeTab);

  const getRowDate = useCallback((r) => r.date || r.startDate || r.effectiveDate, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'name', 'designation', 'diocese', 'outgoing', 'incoming']
  );
  const page = usePagination(filtered);


  const resetForms = () => {
    setTenureForm(EMPTY_TENURE);
    setPostForm(EMPTY_POSTING);
    setHandoverForm(EMPTY_HANDOVER);
    setEditingId(null);
  };

  const switchTab = (tab) => {
    setActiveView(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: its tab (Deleted when it is
  // gone), then its details.
  useOpenRecordFromLink(({ id, ref }) => {
    const row = findLedgerRow(ledger.rows, id, ref);
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const key = row.__key as PastorTab;
    switchTab(key);
    if ((ledger.voided(key) as unknown[]).includes(row)) setViewMode('deleted');
    setViewing(row);
  }, !ledger.loading);

  const startEdit = (item) => {
    if (activeTab === 'tenure') {
      setTenureForm({
        name: item.name || '', designation: item.designation || '', apptOrder: item.apptOrder || '',
        startDate: item.startDate || '', endDate: item.endDate || '',
        prevChurch: item.prevChurch || '', nextChurch: item.nextChurch || '',
        // `status` on a row is the ACTIVE/VOID flag; the tenure's own status is tenureStatus.
        status: item.tenureStatus || 'Transferred', remarks: item.remarks || ''
      });
    } else if (activeTab === 'posting') {
      setPostForm({
        name: item.name || '', designation: item.designation || '', diocese: item.diocese || '',
        orderDate: item.orderDate || '', effectiveDate: item.effectiveDate || '', reason: item.reason || ''
      });
    } else {
      setHandoverForm({ ...EMPTY_HANDOVER, ...item, status: item.handoverStatus || 'Pending' });
    }
    setEditingId(item.rawId);
    setViewMode('add');
  };

  const existingRecordId = (key) =>
    isEditing
      ? (ledger.active(key).find((r) => r.rawId === editingId) as { recordId?: string } | undefined)?.recordId
      : null;

  const handleTenureSubmit = async (e) => {
    e.preventDefault();
    const designation = tenureForm.designation.trim();
    if (!designation) { notify('Designation is required.', 'error'); return; }
    designations.ensure(designation);

    const payload = {
      ...tenureForm,
      designation,
      tenureStatus: tenureForm.status,
      recordId: existingRecordId('tenure') || ledger.nextSequence('tenure'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('tenure', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const handlePostingSubmit = async (e) => {
    e.preventDefault();
    const designation = postForm.designation.trim();
    if (!designation) { notify('Designation is required.', 'error'); return; }
    designations.ensure(designation);

    const existing = isEditing
      ? ledger.active('posting').find((r) => r.rawId === editingId)
      : null;

    // A church has one sitting Pastor: creating a new Current posting archives
    // whoever held it, so two rows can never both read "Current". Both are
    // saved together (or sent for approval together).
    let archived = null;
    const archive: BatchOperation[] = [];
    if (!isEditing && designation === 'Pastor') {
      const sitting = ledger.active('posting')
        .find((p) => p.designation === 'Pastor' && p.postingStatus === 'Current');
      if (sitting) {
        const { __key: _k, __raw: _r, recordType: _t, rawId, ...fields } = sitting;
        archive.push(batch.change(postingsApi, rawId, { ...fields, id: rawId, postingStatus: 'Archived' }));
        archived = sitting.name;
      }
    }

    const payload = {
      ...postForm,
      designation,
      // Editing must not silently promote an archived posting back to Current.
      postingStatus: existing?.postingStatus || 'Current',
      recordId: existingRecordId('posting') || ledger.nextSequence('posting'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('posting', payload, editingId,
      { operations: archive, summary: `New Pastor posting: ${payload.name || designation}; ${archived} archived` })) {
      if (archived) notify(`Saved. The previous posting for ${archived} is archived.`, 'success');
      resetForms();
      setViewMode('history');
    }
  };

  const handleHandoverSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...handoverForm,
      handoverStatus: handoverForm.status,
      recordId: existingRecordId('handover') || ledger.nextSequence('handover'),
      status: 'ACTIVE' as const
    };

    if (await ledger.save('handover', payload, editingId)) { resetForms(); setViewMode('history'); }
  };

  const sub = { fontSize: '12px', color: t.textFaint };
  const fine = { fontSize: '11px', color: t.textFaint };

  const serialColumn = {
    key: 'serial',
    header: 'S.No',
    nowrap: true,
    render: (r, idx) => (
      <>
        <strong>{(page.currentPage - 1) * page.itemsPerPage + idx + 1}</strong>
        <br /><span style={fine}>{r.recordId}</span>
      </>
    )
  };


  const nameCell = (r) => (<><strong>{r.name}</strong><br /><span style={sub}>{r.designation}</span></>);

  // A long note would stretch its row to a column of single words; the table
  // shows the first two lines and View shows it whole.
  const notePreview = (text?: string) => (text
    ? (
      <span
        title={text}
        style={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', maxWidth: '200px', whiteSpace: 'normal'
        }}
      >
        {text}
      </span>
    )
    : '-');

  const tenureTone = (v) => (v === 'Current' ? 'success' : v === 'Deceased' ? 'error' : 'neutral');

  const columnsByTab = {
    tenure: [
      serialColumn,
      { key: 'name', header: 'Pastor Name & ID', render: nameCell },
      {
        // Start, end and how long, in one cell — a column each pushed the
        // actions off the right edge of the page.
        key: 'dates',
        header: 'Tenure (Start – End)',
        nowrap: true,
        render: (r) => (
          <>
            {formatDate(r.startDate)} – {r.endDate ? formatDate(r.endDate) : 'Present'}
            <br /><strong style={{ fontSize: '12px' }}>{durationBetween(r.startDate, r.endDate)}</strong>
          </>
        )
      },
      {
        key: 'status',
        header: 'Status & Church Transfer',
        render: (r) => (
          <>
            <Badge tone={tenureTone(r.tenureStatus)}>{r.tenureStatus || '-'}</Badge>
            <div style={{ ...fine, marginTop: '4px' }}>
              From: {r.prevChurch || '-'}<br />To: {r.nextChurch || '-'}
            </div>
          </>
        )
      },
      // Remarks / Legacy Notes: first two lines here, in full under View.
      { key: 'remarks', header: 'Remarks', render: (r) => notePreview(r.remarks) }
    ],
    posting: [
      serialColumn,
      { key: 'name', header: 'Pastor Name & ID', render: nameCell },
      { key: 'diocese', header: 'Diocese', render: (r) => r.diocese || '-' },
      {
        key: 'effectiveDate',
        header: 'Effective Date',
        nowrap: true,
        render: (r) => (<>{formatDate(r.effectiveDate)}<br /><span style={fine}>Order: {formatDate(r.orderDate)}</span></>)
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <Badge tone={r.postingStatus === 'Current' ? 'success' : 'neutral'}>{r.postingStatus || '-'}</Badge>
      },
      // Transfer Reason / Notes is captured on the posting form but was never
      // shown in the table (CHM-152).
      { key: 'reason', header: 'Reason / Notes', render: (r) => notePreview(r.reason) }
    ],
    handover: [
      serialColumn,
      { key: 'date', header: 'Date', nowrap: true, render: (r) => <strong>{formatDate(r.date)}</strong> },
      { key: 'outgoing', header: 'Outgoing Pastor', render: (r) => <span style={{ color: t.danger, fontWeight: 'bold' }}>{r.outgoing}</span> },
      { key: 'incoming', header: 'Incoming Pastor', render: (r) => <span style={{ color: t.income, fontWeight: 'bold' }}>{r.incoming}</span> },
      {
        key: 'status',
        header: 'Status & Signatures',
        render: (r) => (
          <>
            <Badge tone={r.handoverStatus === 'Completed' ? 'success' : 'warning'}>{r.handoverStatus || '-'}</Badge>
            <div style={{ ...fine, marginTop: '4px' }}>
              Witness: {r.witness || '-'}<br />Diocese: {r.approvedBy || '-'}
            </div>
          </>
        )
      }
    ]
  };

  const viewButton = (r) => (
    <Button variant="ghost" size="sm" title="View all details" aria-label="View all details" onClick={() => setViewing(r)}>
      👁️
    </Button>
  );

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={fine}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        { key: 'view', header: '', align: 'right', render: viewButton }
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          nowrap: true,
          render: (r) => (
            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
              {viewButton(r)}
              <Button write variant="ghost" size="sm" title="Edit" aria-label="Edit" onClick={() => startEdit(r)}>✎</Button>
              <Button variant="danger" size="sm" style={styles.deleteBtn} title="Delete" aria-label="Delete" onClick={() => ledger.requestVoid(r)}>🗑️</Button>
            </div>
          )
        }
      ];

  // Created By is on the View panel: as a column it pushed the actions off
  // the right edge of the page.
  const columns = [
    ...columnsByTab[activeTab],
    ...tailColumns
  ];

  const designationField = (value, onChange) => (
    <SearchableSelect
      label="Designation" required placeholder="Search or type designation..."
      value={value}
      onChange={onChange}
      options={designations.options}
      onOptionsChange={designations.setOptions}
    />
  );

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      {/* The handover register carries its own template and print scope. */}

      <div>
        <PageHeader
          icon="📜"
          title="Pastor Turnover"
          activeTab={activeView}
          onTabChange={switchTab}
          tabs={[
            { key: 'summary', label: '📊 Summary' },
            { key: 'tenure', label: '📜 Tenure History' },
            { key: 'posting', label: '📨 Postings & Orders' },
            { key: 'handover', label: '🤝 Handover Register' }
          ]}
        />

        {isSummary && (
          <PastorSummary
            tenures={ledger.active('tenure')}
            postings={ledger.active('posting')}
            handovers={ledger.active('handover')}
            loading={ledger.loading}
            isDarkMode={isDarkMode}
          />
        )}

        {!isSummary && (
        <SubTabs
          value={viewMode}
          onChange={switchView}
          tabs={[
            { key: 'history', label: '📋 View Active Records' },
            { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
            { key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }
          ]}
        />
        )}

        {!isSummary && (viewMode === 'history' || isDeletedView) && (
          <>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); page.reset(); }}
              onClear={() => { clear(); page.reset(); }}
              placeholder="Search by Pastor, Designation, Diocese or ID..."
              actions={
                <>
                  <DateRangeFilter {...range} onChange={page.reset} />
                  <PrintButton
                    module="Pastor Turnover"
                    documentType={(({ tenure: { doc: 'PASTOR_REGISTER', title: 'Pastor Tenure History' }, posting: { doc: 'PASTOR_POSTING_REGISTER', title: 'Pastor Postings & Orders' }, handover: { doc: 'PASTOR_HANDOVER_REGISTER', title: 'Pastor Handover Register' } })[activeTab]).doc}
                    size="sm"
                    title={(({ tenure: { doc: 'PASTOR_REGISTER', title: 'Pastor Tenure History' }, posting: { doc: 'PASTOR_POSTING_REGISTER', title: 'Pastor Postings & Orders' }, handover: { doc: 'PASTOR_HANDOVER_REGISTER', title: 'Pastor Handover Register' } })[activeTab]).title.replace(/[^A-Za-z0-9]+/g, '-')}
                    // Each tab prints its own register, with every row that matches the
                    // filters — dates formatted and Created By on every line.
                    data={() => ({
                      title: `${(({ tenure: { doc: 'PASTOR_REGISTER', title: 'Pastor Tenure History' }, posting: { doc: 'PASTOR_POSTING_REGISTER', title: 'Pastor Postings & Orders' }, handover: { doc: 'PASTOR_HANDOVER_REGISTER', title: 'Pastor Handover Register' } })[activeTab]).title}${isDeletedView ? ' (Deleted)' : ''}`,
                      reference: printReference(searchTerm && `Search: "${searchTerm}"`),
                      rowCount: filtered.length,
                      rows: printableRows(filtered)
                    })}
                    onNotify={notify}
                    isDarkMode={isDarkMode}
                  />
                </>
              }
            />

            {/* Stood down while a handover preview is open — that owns the page. */}
            <TablePanel
              title={<>Pastor Turnover — {activeTab === 'tenure' ? 'Tenure Records' : activeTab === 'posting' ? 'Postings' : 'Handover Register'}</>}
              below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
                columns={columns}
                rows={page.currentItems}
                rowKey="rawId"
                loading={ledger.loading}
                emptyMessage="No records found for this category."
            />
          </>
        )}

        {/* Every field of one record — the table only has room for a summary. */}
        {viewing && (
          <Drawer
            open
            title={activeTab === 'handover'
              ? `${viewing.outgoing || '-'} → ${viewing.incoming || '-'}`
              : viewing.name || viewing.recordId}
            subtitle={`${viewing.recordId || ''} · ${activeTab === 'tenure' ? 'Tenure' : activeTab === 'posting' ? 'Posting' : 'Handover'}`}
            onClose={() => setViewing(null)}
            isDarkMode={isDarkMode}
            footer={!isDeletedView && (
              <Button write fullWidth onClick={() => { const r = viewing; setViewing(null); startEdit(r); }}>✎ Edit</Button>
            )}
          >
            {activeTab === 'tenure' && (
              <>
                <Drawer.Section title="Pastor" />
                <Drawer.Row label="Pastor Name" value={viewing.name} />
                <Drawer.Row label="Designation" value={viewing.designation} />
                <Drawer.Row label="Appointment Order" value={viewing.apptOrder || '-'} />
                <Drawer.Section title="Tenure" />
                <Drawer.Row label="Start Date" value={formatDate(viewing.startDate)} />
                <Drawer.Row label="End Date" value={viewing.endDate ? formatDate(viewing.endDate) : 'Present'} />
                <Drawer.Row label="Duration" value={durationBetween(viewing.startDate, viewing.endDate)} />
                <Drawer.Row label="Status">
                  <Badge tone={tenureTone(viewing.tenureStatus)}>{viewing.tenureStatus || '-'}</Badge>
                </Drawer.Row>
                <Drawer.Row label="Previous Church" value={viewing.prevChurch || '-'} />
                <Drawer.Row label="Next Church" value={viewing.nextChurch || '-'} />
                <Drawer.Section title="Remarks / Legacy Notes" />
                <p style={{ whiteSpace: 'pre-wrap', color: t.textMain, margin: '4px 0 8px', lineHeight: 1.6 }}>{viewing.remarks || '-'}</p>
              </>
            )}
            {activeTab === 'posting' && (
              <>
                <Drawer.Section title="Posting" />
                <Drawer.Row label="Pastor Name" value={viewing.name} />
                <Drawer.Row label="Designation" value={viewing.designation} />
                <Drawer.Row label="Diocese" value={viewing.diocese || '-'} />
                <Drawer.Row label="Order Date" value={formatDate(viewing.orderDate)} />
                <Drawer.Row label="Effective Date" value={formatDate(viewing.effectiveDate)} />
                <Drawer.Row label="Status">
                  <Badge tone={viewing.postingStatus === 'Current' ? 'success' : 'neutral'}>{viewing.postingStatus || '-'}</Badge>
                </Drawer.Row>
                <Drawer.Section title="Reason / Notes" />
                <p style={{ whiteSpace: 'pre-wrap', color: t.textMain, margin: '4px 0 8px', lineHeight: 1.6 }}>{viewing.reason || '-'}</p>
              </>
            )}
            {activeTab === 'handover' && (
              <>
                <Drawer.Section title="Handover" />
                <Drawer.Row label="Date" value={formatDate(viewing.date)} />
                <Drawer.Row label="Outgoing Pastor" value={viewing.outgoing || '-'} />
                <Drawer.Row label="Incoming Pastor" value={viewing.incoming || '-'} />
                <Drawer.Row label="Witness" value={viewing.witness || '-'} />
                <Drawer.Row label="Approved By (Diocese)" value={viewing.approvedBy || '-'} />
                <Drawer.Row label="Status">
                  <Badge tone={viewing.handoverStatus === 'Completed' ? 'success' : 'warning'}>{viewing.handoverStatus || '-'}</Badge>
                </Drawer.Row>
              </>
            )}
            <Drawer.Section title="Record" />
            <Drawer.Row label="Record ID" value={viewing.recordId || '-'} />
            <Drawer.Row label="Created By" value={viewing.createdBy || '-'} />
            {isDeletedView && (
              <>
                <Drawer.Row label="Deleted On" value={viewing.deletedAt || '-'} />
                <Drawer.Row label="Delete Reason" value={viewing.deleteReason || '-'} />
              </>
            )}
          </Drawer>
        )}

        {!isSummary && viewMode === 'add' && activeTab === 'tenure' && (
          <FormLayout
            title={isEditing ? 'Edit Tenure' : 'Record Pastoral Tenure'}
            submitLabel={isEditing ? 'Update Tenure' : 'Save Tenure'}
            submitting={ledger.saving}
            onSubmit={handleTenureSubmit}
            onCancel={isEditing ? () => switchView('history') : undefined}
            cancelLabel="Cancel Edit"
          >
            <FormLayout.Grid>
              <Input
                label="Pastor Name" required placeholder="e.g. Rev. John Samuel"
                value={tenureForm.name}
                onChange={(e) => setTenureForm({ ...tenureForm, name: e.target.value })}
                style={{ fontWeight: 'bold' }}
              />
              {designationField(tenureForm.designation, (v) => setTenureForm((prev) => ({ ...prev, designation: v })))}
              <Input
                label="Appointment Order No." placeholder="e.g. DIO/2024/112"
                value={tenureForm.apptOrder}
                onChange={(e) => setTenureForm({ ...tenureForm, apptOrder: e.target.value })}
              />
              <Input
                label="Start Date (Joined)" type="date" required
                value={tenureForm.startDate}
                onChange={(e) => setTenureForm({ ...tenureForm, startDate: e.target.value })}
              />
              <Input
                label="End Date (Relieved)" type="date"
                hint="Leave blank for the current pastor — duration runs to today."
                value={tenureForm.endDate}
                onChange={(e) => setTenureForm({ ...tenureForm, endDate: e.target.value })}
              />
              <Select
                label="Final Status" placeholder=""
                options={TENURE_STATUSES}
                value={tenureForm.status}
                onChange={(e) => setTenureForm({ ...tenureForm, status: e.target.value })}
              />
              <Input
                label="Previous Church (If known)" placeholder="Church transferred from"
                value={tenureForm.prevChurch}
                onChange={(e) => setTenureForm({ ...tenureForm, prevChurch: e.target.value })}
              />
              <Input
                label="Next Church (If transferred)" placeholder="Church transferred to"
                value={tenureForm.nextChurch}
                onChange={(e) => setTenureForm({ ...tenureForm, nextChurch: e.target.value })}
              />
              <Input
                label="Remarks / Legacy Notes" type="textarea" rows={2} fullWidth
                placeholder="Key contributions during the tenure..."
                value={tenureForm.remarks}
                onChange={(e) => setTenureForm({ ...tenureForm, remarks: e.target.value })}
              />
            </FormLayout.Grid>
          </FormLayout>
        )}

        {!isSummary && viewMode === 'add' && activeTab === 'posting' && (
          <FormLayout
            title={isEditing ? 'Edit Posting Order' : 'Record Posting / Transfer Order'}
            submitLabel={isEditing ? 'Update Posting' : 'Save Posting'}
            submitting={ledger.saving}
            onSubmit={handlePostingSubmit}
            onCancel={isEditing ? () => switchView('history') : undefined}
            cancelLabel="Cancel Edit"
          >
            <FormLayout.Grid>
              <Input
                label="Pastor Name" required placeholder="e.g. Rev. John Samuel"
                value={postForm.name}
                onChange={(e) => setPostForm({ ...postForm, name: e.target.value })}
                style={{ fontWeight: 'bold' }}
              />
              {designationField(postForm.designation, (v) => setPostForm((prev) => ({ ...prev, designation: v })))}
              <Input
                label="Diocese" placeholder="e.g. Madras Diocese"
                value={postForm.diocese}
                onChange={(e) => setPostForm({ ...postForm, diocese: e.target.value })}
              />
              <Input
                label="Order Date" type="date" required
                value={postForm.orderDate}
                onChange={(e) => setPostForm({ ...postForm, orderDate: e.target.value })}
              />
              <Input
                label="Effective Joining Date" type="date" required
                value={postForm.effectiveDate}
                onChange={(e) => setPostForm({ ...postForm, effectiveDate: e.target.value })}
              />
              <Input
                label="Transfer Reason / Notes" type="textarea" rows={2} fullWidth
                placeholder="Reason recorded on the order..."
                value={postForm.reason}
                onChange={(e) => setPostForm({ ...postForm, reason: e.target.value })}
              />
            </FormLayout.Grid>
          </FormLayout>
        )}

        {!isSummary && viewMode === 'add' && activeTab === 'handover' && (
          <FormLayout
            title={isEditing ? 'Edit Handover Log' : 'Execute Handover / Takeover Checklist'}
            submitLabel={isEditing ? 'Update Handover Log' : 'Save Handover'}
            submitting={ledger.saving}
            onSubmit={handleHandoverSubmit}
            onCancel={isEditing ? () => switchView('history') : undefined}
            cancelLabel="Cancel Edit"
            maxWidth="900px"
          >
            <FormLayout.Grid>
              <Input
                label="Handover Date" type="date" required fullWidth
                value={handoverForm.date}
                onChange={(e) => setHandoverForm({ ...handoverForm, date: e.target.value })}
              />
              <Input
                label="Outgoing / Relieving Pastor" required placeholder="Relieving pastor"
                value={handoverForm.outgoing}
                onChange={(e) => setHandoverForm({ ...handoverForm, outgoing: e.target.value })}
                style={{ color: t.danger, fontWeight: 'bold' }}
              />
              <Input
                label="Incoming / New Pastor" required placeholder="New pastor"
                value={handoverForm.incoming}
                onChange={(e) => setHandoverForm({ ...handoverForm, incoming: e.target.value })}
                style={{ color: t.income, fontWeight: 'bold' }}
              />
              <Input
                label="Witness (Church Secretary / Committee)" placeholder="Name of witness"
                value={handoverForm.witness}
                onChange={(e) => setHandoverForm({ ...handoverForm, witness: e.target.value })}
              />
              <Input
                label="Approved By (Diocese / Bishop)" placeholder="Approving authority"
                value={handoverForm.approvedBy}
                onChange={(e) => setHandoverForm({ ...handoverForm, approvedBy: e.target.value })}
              />

              {HANDOVER_SECTIONS.map((section, index) => (
                <div key={section.title} style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                  <h4 style={{
                    margin: '0 0 12px 0', color: t.textMain,
                    borderBottom: `1px solid ${t.border}`, paddingBottom: '5px'
                  }}>
                    {index + 1}. {section.title}
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                    {section.items.map((item) => (
                      <label
                        key={item.key}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          fontSize: t.fontSize.md, cursor: 'pointer', color: t.textMain,
                          backgroundColor: t.bgSubtle, padding: '10px', borderRadius: t.radius.md
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(handoverForm[item.key])}
                          onChange={(e) => setHandoverForm({ ...handoverForm, [item.key]: e.target.checked })}
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <Select
                label="Final Handover Status" placeholder="" fullWidth
                options={HANDOVER_STATUSES}
                value={handoverForm.status}
                onChange={(e) => setHandoverForm({ ...handoverForm, status: e.target.value })}
              />
            </FormLayout.Grid>
          </FormLayout>
        )}
      </div>

      <PromptDialog
        open={Boolean(ledger.voiding)}
        title={`Delete ${ledger.voiding?.recordType || ''} ${ledger.voiding?.recordId || ''}`}
        message="Please enter the reason for voiding this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={(reason) => ledger.confirmVoid(reason)}
        onCancel={ledger.cancelVoid}
      />
    </div>
  );
};

export default PastorHistory;
